import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Modal } from 'components/Modal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  PlusIcon,
  TemplateIcon,
  LightningBoltIcon,
  FilterIcon,
  CheckCircleIcon,
  RefreshIcon,
  CreditCardIcon,
  BanIcon,
  ChatAlt2Icon,
  SparklesIcon,
} from '@heroicons/react/outline';
import { VisualRuleConditionCard } from './VisualRuleConditionCard';
import {
  getRuleNaturalLanguageSummary,
  getPrebuiltTemplates,
  ruleActionOptions,
} from '../../containers/NestedContainers/ClaimAutomation/utils';

export interface VisualRulePipelineBuilderProps {
  rule: any;
  onRuleChange: (updatedRule: any) => void;
  disabled?: boolean;
}

export const VisualRulePipelineBuilder: React.FC<
  VisualRulePipelineBuilderProps
> = ({ rule, onRuleChange, disabled = false }) => {
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const statements = React.useMemo(() => {
    return (rule.statements || []).map((stmt: any, idx: number) => {
      if (!stmt._uid) {
        return {
          ...stmt,
          _uid: `stmt-${idx}-${stmt.expressionField || 'f'}-${
            stmt.expression || 'e'
          }`,
        };
      }
      return stmt;
    });
  }, [rule.statements]);

  const handleDragEnd = (event: DragEndEvent) => {
    if (disabled) return;
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = statements.findIndex(
        (stmt: any) => stmt._uid === active.id,
      );
      const newIndex = statements.findIndex(
        (stmt: any) => stmt._uid === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(statements, oldIndex, newIndex);
        // Normalize first statement connector to empty
        const normalized = reordered.map((stmt: any, idx: number) => ({
          ...stmt,
          connector: idx === 0 ? '' : stmt.connector || 'AND',
        }));
        onRuleChange({ ...rule, statements: normalized });
      }
    }
  };

  const handleStatementChange = (
    index: number,
    changes: Record<string, any>,
  ) => {
    if (disabled) return;
    const updated = [...statements];
    updated[index] = {
      ...updated[index],
      ...changes,
    };
    onRuleChange({ ...rule, statements: updated });
  };

  const handleAddCondition = () => {
    if (disabled) return;
    const newStatement = {
      _uid: `stmt-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      expressionField: '',
      expression: '',
      expressionValue: '',
      connector: statements.length === 0 ? '' : 'AND',
      config: { field: 'stringInput', placeHolder: 'Value' },
    };
    onRuleChange({
      ...rule,
      statements: [...statements, newStatement],
    });
  };

  const handleRemoveCondition = (index: number) => {
    if (disabled) return;
    const filtered = statements.filter((_: any, idx: number) => idx !== index);
    const normalized = filtered.map((stmt: any, idx: number) => ({
      ...stmt,
      connector: idx === 0 ? '' : stmt.connector || 'AND',
    }));
    onRuleChange({ ...rule, statements: normalized });
  };

  const handleToggleConnector = (index: number) => {
    if (disabled || index === 0) return;
    const current = statements[index]?.connector || 'AND';
    const next = current === 'AND' ? 'OR' : 'AND';
    handleStatementChange(index, { connector: next });
  };

  const handleApplyTemplate = (template: any) => {
    if (disabled) return;
    onRuleChange({
      ...rule,
      title: template.title,
      action: template.action,
      refundShipping: template.refundShipping,
      notify: template.notify,
      actionNotes: template.actionNotes,
      statements: template.statements,
    });
    setShowTemplatesModal(false);
  };

  const primaryActions = [
    {
      id: 'REFUND',
      name: 'Auto-Refund',
      description: 'Issue refund to original payment method',
      icon: RefreshIcon,
      activeColor: 'ring-2 ring-emerald-500 border-emerald-500 bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      id: 'REORDER',
      name: 'Auto-Reorder',
      description: 'Create a replacement fulfillment order',
      icon: CheckCircleIcon,
      activeColor: 'ring-2 ring-blue-500 border-blue-500 bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      id: 'STORE_CREDIT',
      name: 'Store Credit',
      description: 'Issue store credit / digital coupon',
      icon: CreditCardIcon,
      activeColor: 'ring-2 ring-purple-500 border-purple-500 bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      id: 'DENY',
      name: 'Auto-Deny',
      description: 'Automatically reject the claim',
      icon: BanIcon,
      activeColor: 'ring-2 ring-rose-500 border-rose-500 bg-rose-100',
      iconColor: 'text-rose-600',
    },
  ];

  const templates = getPrebuiltTemplates();

  return (
    <div className="space-y-6">
      {/* Rule Name & Active Status */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
              Rule Name <span className="text-red-500">*</span>
            </label>
            <input
              aria-label="Rule name"
              type="text"
              disabled={disabled}
              className="block w-full rounded-lg border border-slate-300 px-3.5 py-2 text-base font-medium shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
              placeholder="e.g. Auto-Refund Damaged Items Under $25"
              value={rule.title || ''}
              onChange={(e) => {
                if (disabled) return;
                onRuleChange({ ...rule, title: e.target.value });
              }}
            />
          </div>

          <div className="flex items-center gap-3 sm:border-l sm:border-slate-200 sm:pl-6">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-slate-900">
                Rule Status
              </span>
              <span className="text-xs text-slate-500">
                {rule.active
                  ? 'Active & evaluating claims'
                  : 'Paused / Inactive'}
              </span>
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onRuleChange({ ...rule, active: !rule.active });
              }}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
              } ${rule.active ? 'bg-blue-600' : 'bg-slate-300'}`}
              role="switch"
              aria-label="Rule active"
              aria-checked={Boolean(rule.active)}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  rule.active ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* PIPELINE CONTAINER */}
      <div className="relative pl-6 sm:pl-8 before:absolute before:left-3 sm:before:left-4 before:top-4 before:bottom-4 before:w-0.5 before:bg-slate-300">
        {/* STEP 1: TRIGGER */}
        <div className="relative mb-6">
          <div className="absolute -left-6 sm:-left-8 top-1.5 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center ring-4 ring-slate-100">
            <LightningBoltIcon className="w-3.5 h-3.5" />
          </div>

          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">
                  When (Trigger)
                </span>
                <h4 className="text-sm font-semibold text-slate-900 mt-0.5">
                  When a customer submits a new claim
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Evaluated automatically when a customer submits a claim
                  through the portal.
                </p>
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  setShowTemplatesModal(true);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-xs font-medium text-slate-700 shadow-sm transition-colors ${
                  disabled
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:bg-slate-50'
                }`}
              >
                <TemplateIcon className="w-4 h-4 text-slate-500" />
                Templates
              </button>
            </div>
          </div>
        </div>

        {/* STEP 2: CONDITIONS (IF) */}
        <div className="relative mb-6">
          <div className="absolute -left-6 sm:-left-8 top-1.5 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center ring-4 ring-slate-100">
            <FilterIcon className="w-3.5 h-3.5" />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                  If (Conditions)
                </span>
                <p className="text-xs text-slate-500">
                  Drag cards to reorder. Rule matches when conditions are
                  satisfied.
                </p>
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  handleAddCondition();
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold transition-colors ${
                  disabled
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:bg-blue-100'
                }`}
              >
                <PlusIcon className="w-3.5 h-3.5" />+ Add Condition
              </button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={statements.map((stmt: any) => stmt._uid)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {statements.map((statement: any, index: number) => (
                    <React.Fragment key={statement._uid}>
                      {index > 0 && (
                        <div className="flex items-center justify-center my-1.5">
                          <button
                            type="button"
                            disabled={disabled}
                            onClick={() => {
                              if (disabled) return;
                              handleToggleConnector(index);
                            }}
                            className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border border-slate-300 bg-white text-slate-700 shadow-xs transition-colors ${
                              disabled
                                ? 'opacity-60 cursor-not-allowed'
                                : 'hover:bg-slate-100 hover:border-slate-400'
                            }`}
                            title={
                              disabled
                                ? undefined
                                : 'Click to toggle between AND / OR'
                            }
                          >
                            {statement.connector || 'AND'} ▾
                          </button>
                        </div>
                      )}
                      <VisualRuleConditionCard
                        id={statement._uid}
                        index={index}
                        statement={statement}
                        disabled={disabled}
                        onChange={(changes) =>
                          handleStatementChange(index, changes)
                        }
                        onRemove={() => handleRemoveCondition(index)}
                        canRemove={statements.length > 1}
                      />
                    </React.Fragment>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* STEP 3: RESOLUTION ACTION (THEN) */}
        <div className="relative mb-6">
          <div className="absolute -left-6 sm:-left-8 top-1.5 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center ring-4 ring-slate-100">
            <CheckCircleIcon className="w-3.5 h-3.5" />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Then (Resolution Action)
              </span>
              <h4 className="text-sm font-semibold text-slate-900 mt-0.5">
                Automatically resolve with action:
              </h4>
            </div>

            {/* Action Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {primaryActions.map((action) => {
                const isSelected = rule.action === action.id;
                const IconComponent = action.icon;
                return (
                  <button
                    key={action.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      onRuleChange({ ...rule, action: action.id });
                    }}
                    className={`flex flex-col text-left p-3.5 rounded-xl border transition-all duration-150 ${
                      disabled ? 'opacity-60 cursor-not-allowed' : ''
                    } ${
                      isSelected
                        ? action.activeColor
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <IconComponent
                        className={`w-4 h-4 ${
                          isSelected ? action.iconColor : 'text-slate-400'
                        }`}
                      />
                      <span className="text-sm font-semibold text-slate-900">
                        {action.name}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {action.description}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Other resolution options dropdown */}
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500 font-medium">
                Or choose advanced resolution:
              </span>
              <select
                aria-label="Resolution action"
                disabled={disabled}
                className="text-xs rounded-md border border-slate-300 px-2 py-1 text-slate-700 focus:border-blue-500 focus:ring-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
                value={rule.action || ''}
                onChange={(e) => {
                  if (disabled) return;
                  onRuleChange({ ...rule, action: e.target.value });
                }}
              >
                <option value="">Select action...</option>
                {ruleActionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Contextual Action Settings */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3 border border-slate-200/80">
              <h5 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Action Settings
              </h5>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {rule.action === 'REFUND' && (
                  <label className="flex items-center gap-2.5 text-xs text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      disabled={disabled}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-4 w-4 disabled:opacity-60 disabled:cursor-not-allowed"
                      checked={Boolean(rule.refundShipping)}
                      onChange={(e) => {
                        if (disabled) return;
                        onRuleChange({
                          ...rule,
                          refundShipping: e.target.checked,
                        });
                      }}
                    />
                    <span className="font-medium">
                      Refund original shipping costs
                    </span>
                  </label>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Resolution Note / Internal Message
                </label>
                <input
                  aria-label="Resolution note"
                  type="text"
                  disabled={disabled}
                  className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-xs shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
                  placeholder="e.g. Automatically approved via Low-Value Lost policy"
                  value={rule.actionNotes || ''}
                  onChange={(e) => {
                    if (disabled) return;
                    onRuleChange({ ...rule, actionNotes: e.target.value });
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* STEP 4: LIVE SUMMARY BANNER */}
        <div className="relative">
          <div className="absolute -left-6 sm:-left-8 top-1.5 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center ring-4 ring-slate-100">
            <ChatAlt2Icon className="w-3.5 h-3.5" />
          </div>

          <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl border border-indigo-200/80 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1 text-indigo-900 font-semibold text-xs">
              <SparklesIcon className="w-4 h-4 text-indigo-600" />
              <span>Live Rule Preview</span>
            </div>
            <p className="text-sm font-medium text-slate-800 leading-relaxed">
              {getRuleNaturalLanguageSummary(rule)}
            </p>
          </div>
        </div>
      </div>

      {/* TEMPLATES MODAL */}
      {showTemplatesModal && (
        <Modal
          open
          openModal={() => setShowTemplatesModal(false)}
          containerClass="w-full sm:max-w-xl"
        >
          <div className="bg-white w-full max-h-[calc(100vh-15rem)] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 shrink-0">
              <div>
                <Dialog.Title
                  as="h3"
                  className="text-base font-bold text-slate-900"
                >
                  Pre-Built Automation Templates
                </Dialog.Title>
                <p className="text-xs text-slate-500">
                  Choose a starting point, then review its conditions before
                  saving.
                </p>
              </div>
            </div>

            <div className="space-y-3 flex-1 overflow-y-auto pr-1 my-3">
              {templates.map((tpl) => (
                <button
                  type="button"
                  key={tpl.id}
                  disabled={disabled}
                  className={`p-4 rounded-xl border border-slate-200 transition flex flex-wrap gap-3 items-center justify-between group text-left w-full ${
                    disabled
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:border-blue-400 hover:bg-blue-50/30'
                  }`}
                  onClick={() => {
                    if (disabled) return;
                    handleApplyTemplate(tpl);
                  }}
                >
                  <div className="pr-4">
                    <h4 className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                      {tpl.title}
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {tpl.description}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded uppercase bg-slate-100 text-slate-700">
                        Action: {tpl.action}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        {tpl.statements.length} condition
                        {tpl.statements.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1.5 rounded-lg text-white text-xs font-semibold whitespace-nowrap shadow-sm ${
                      disabled
                        ? 'bg-slate-400'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    Use Template
                  </span>
                </button>
              ))}
            </div>

            <div className="pt-2 flex justify-end border-t border-slate-100 shrink-0">
              <button
                type="button"
                onClick={() => setShowTemplatesModal(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
